import { UserRole } from '@/services/user.service';
import { NumberAggregate } from '@/utils/stats';

export type StagePlayerSize = 'MINI' | 'FULL_SCREEN' | 'NORMAL';

export const EVENT_HEARTBEAT_INTERVAL = 30000;

export type NetworkQuality = {
  uplink: NumberAggregate;
  downlink: NumberAggregate;
};

export type PreRecordedVideo = {
  is_simulive_video_playing: Boolean;
  is_live_hybrid_video_playing: Boolean;
  is_live_external_video_playing: Boolean;
};

export interface EventHeartbeatState {
  heartbeatIntervalId: number | null;
  metadata: {
    version: string;
    payload_counter: number;
    session_id: string | undefined;
  };
  event_info: {
    event_id: string;
    event_type: string;
    is_hybrid: Boolean;
    is_available_on_demand: Boolean;
    on_demand_mode: Boolean;
    org_id: string;
  };
  user_data: {
    event_user_id: string;
    last_interaction_time: number;
    is_tab_active: Boolean;
    event_role: UserRole | undefined;
  };
  current_screen_state: {
    is_prerecorded_video_playing: PreRecordedVideo;
    is_on_demand_video_playing: Boolean;
    current_route_name: string;
    stage_player_size: StagePlayerSize;
    broadcast_id: string;
    broadcast_type: string;
    booth_id: string;
    discussion_room_id: string;
    is_live: Boolean;
    video_length: Number | undefined;
    seek_pos: Number | undefined;
    sharing: {
      broadcast_ticker: Boolean;
      broadcast_lower_third: Boolean;
      event_ticker: Boolean;
    };
    asset: null;
    chapter_id: string | null;
  };
  qos: {
    network_quality: NetworkQuality;
    windowFps: NumberAggregate;
    is_litemode_on: Boolean;
    memory: {
      jsHeapSizeLimit: Number;
      totalJSHeapSize: Number;
      usedJSHeapSize: Number;
    };
    agora_metrics: {
      startup_time_ms: number; // time from join-init to first connection established
      reconnect_count: number; // total join retries across session
      disconnection_count: number; // hard disconnects (non-LEAVE)
      error_count: number; // SDK exceptions (low bitrate, mic issues, etc.)
      connection_established_count: number; // total (re)connections established
      avg_connection_time_ms: number; // average time per connection cycle
      is_connected: boolean;
      remote_user_count: number;
      cloud_proxy: boolean;
      current_background_filter: string;
      rtt_ms: number | null; // round-trip time (> 400ms = bad)
      outgoing_available_bandwidth: number | null; // upstream available bandwidth kbps
      uplink_network_quality: number; // 1-6 (1=excellent, 6=disconnected)
      downlink_network_quality: number; // 1-6
      local_audio_send_bitrate: number | null; // kbps — < 10 = muted or bad mic
      local_audio_send_packets_lost: number | null; // upstream packet loss count (null on Safari)
      local_audio_send_jitter_ms: number | null; // ms — upstream audio jitter; > 30 = network instability
      local_audio_send_rtt_ms: number | null; // ms — upstream audio round-trip time; > 300 = poor link
      local_audio_current_packet_loss_rate: number | null; // % — rolling 400ms loss; > 5 = active loss bursts
      local_video_send_bitrate: number | null; // kbps — < 100 = degraded
      local_video_send_fps: number | null; // < 10 fps = frozen/stuttering
      local_video_send_resolution_height: number | null; // unexpected drops = ABR downgrade
      local_video_encode_delay: number | null; // ms — > 50 = CPU bottleneck
      local_video_total_freeze_time: number | null; // cumulative freeze seconds
      local_video_target_send_bitrate: number | null; // compare to actual to detect throttling
      screenshare_send_bitrate: number | null; // kbps
      screenshare_send_fps: number | null; // low = laggy for viewers
      screenshare_total_freeze_time: number | null;
      worst_remote_video_packet_loss_rate: number | null; // % — > 10 = bad session
      worst_remote_video_freeze_rate: number | null; // % — > 5 = visible freezes
      avg_remote_video_receive_bitrate: number | null; // kbps — low = bandwidth-starved
      worst_remote_video_end2end_delay: number | null; // ms — > 400 = noticeable lag
      worst_remote_audio_packet_loss_rate: number | null; // % — cumulative; > 5 = choppy audio
      worst_remote_audio_freeze_rate: number | null; // % — audio freezes very noticeable
      worst_remote_audio_current_packet_loss_rate: number | null; // % — rolling 400ms loss; > 5 = active jitter
      worst_remote_audio_end2end_delay: number | null; // ms — capture-to-playback delay; > 400 = noticeable
      total_remote_audio_packets_discarded: number | null; // packets dropped by jitter buffer across all remotes
    } | null;
    simulive_hls_metrics: {
      startup_time_ms: number; // time from connectionInitialized to first play
      buffer_count: number; // all stalls (onseeking fires for HLS.js internal ops, not user seeks — controls are disabled)
      genuine_buffer_duration_ms: number;
      total_dropped_frames: number; // fps_drop_count removed — magnitude covered by this field
      avg_live_latency_s: number; // Welford running average of HLS latency
      non_fatal_error_count: number;
      level_switch_down_count: number; // quality degradations only; total level_switch_count removed — upward switches are healthy
      playback_restart_count: number; // forced purge-and-restart cycles
      avg_frag_load_time_ms: number; // Welford running average of fragment load time
      current_latency_s: number | null;
      current_bandwidth_bps: number | null; // HLS.js bandwidthEstimate; current_level index removed — meaningless without levels[] lookup
      buffer_length_s: number | null; // seconds of buffered content ahead — low value = stall risk
    } | null;
    ondemand_hls_metrics: {
      startup_time_ms: number; // MANIFEST_PARSED → first playing event
      rebuffer_count: number; // all onwaiting stalls (incl. seek-induced)
      rebuffer_duration_ms: number;
      total_dropped_frames: number;
      level_switch_down_count: number; // ABR downgrades only
      avg_frag_load_time_ms: number;
      non_fatal_error_count: number;
      seek_count: number; // total user seeks
      forward_skip_count: number; // seeks where to > from
      rewind_count: number; // seeks where to < from
      pause_count: number;
      completion_rate: number | null; // currentTime / videoLength
      current_position_s: number | null;
      current_playback_speed: number | null; // active speed at heartbeat tick (1 = normal)
      current_bandwidth_bps: number | null;
      buffer_length_s: number | null;
    } | null;
    live_hls_metrics: {
      startup_time_ms: number; // connectionInitialized → first playing event
      buffer_count: number; // all onwaiting stalls
      buffer_duration_ms: number;
      total_dropped_frames: number;
      level_switch_down_count: number; // ABR downgrades only
      avg_frag_load_time_ms: number;
      non_fatal_error_count: number;
      sync_correction_count: number; // times player was resynced to server time
      current_bandwidth_bps: number | null;
      buffer_length_s: number | null;
    } | null;
    rtmp_hls_metrics: {
      startup_time_ms: number;
      buffer_count: number;
      genuine_buffer_duration_ms: number;
      total_dropped_frames: number;
      avg_live_latency_s: number;
      non_fatal_error_count: number;
      level_switch_down_count: number;
      playback_restart_count: number;
      avg_frag_load_time_ms: number;
      current_latency_s: number | null;
      current_bandwidth_bps: number | null;
      buffer_length_s: number | null;
    } | null;
  };
  device: {
    is_mobile: Boolean;
    os: string;
    cores: number;
    browser: string;
    browser_version: string;
    screen_width: Number;
    screen_height: number;
  };
}

export const defaultState: EventHeartbeatState = {
  heartbeatIntervalId: null,
  metadata: {
    version: '1.1.0',
    payload_counter: 0,
    session_id: ''
  },
  event_info: {
    event_id: '',
    event_type: '',
    is_hybrid: false,
    is_available_on_demand: false,
    on_demand_mode: false,
    org_id: ''
  },
  user_data: {
    event_user_id: '',
    last_interaction_time: 0,
    is_tab_active: true,
    event_role: undefined
  },
  current_screen_state: {
    is_prerecorded_video_playing: {
      is_live_hybrid_video_playing: false,
      is_simulive_video_playing: false,
      is_live_external_video_playing: false
    },
    is_on_demand_video_playing: false,
    current_route_name: '',
    stage_player_size: 'NORMAL',
    broadcast_id: '',
    broadcast_type: '',
    booth_id: '',
    discussion_room_id: '',
    is_live: false,
    video_length: 0,
    seek_pos: 0,
    sharing: {
      broadcast_ticker: false,
      broadcast_lower_third: false,
      event_ticker: false
    },
    asset: null,
    chapter_id: null
  },
  qos: {
    network_quality: {
      uplink: {
        current: 0,
        median: 0,
        min: 0,
        max: 0,
        average: 0,
        mode: 0
      },
      downlink: {
        current: 0,
        median: 0,
        min: 0,
        max: 0,
        average: 0,
        mode: 0
      }
    },
    windowFps: {
      current: 0,
      median: 0,
      min: 0,
      max: 0,
      average: 0,
      mode: 0
    },
    is_litemode_on: false,
    memory: {
      jsHeapSizeLimit: 0,
      totalJSHeapSize: 0,
      usedJSHeapSize: 0
    },
    agora_metrics: null,
    simulive_hls_metrics: null,
    ondemand_hls_metrics: null,
    live_hls_metrics: null,
    rtmp_hls_metrics: null
  },
  device: {
    is_mobile: false,
    os: '',
    cores: 0,
    browser: '',
    browser_version: '',
    screen_width: 0,
    screen_height: 0
  }
};
